import {
  createContext,
  useRef,
  useMemo,
  useLayoutEffect,
  useState,
  memo,
  ReactNode,
  PropsWithChildren
} from 'react'
import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type { ApolloUseQueryResult } from '@vivaldis/graphql'
import { employeeQuery } from '../queries/employeeQuery'
import {
  EmployeeQuery,
  EmployeeQueryVariables
} from '../queries/__generated__/EmployeeQuery'
import { UserType } from '../typings/UserType'
import { useLocale } from '@vivaldis/common'
import { useSession } from '../hooks/useSession'
import { useHasSession } from '../hooks/useHasSession'
import { useGraphQLBrand } from '../hooks/useGraphQLBrand'

export type EmployeeQueryContextValue = ApolloUseQueryResult<
  EmployeeQuery,
  EmployeeQueryVariables,
  'empty' | 'complete' | 'streaming',
  EmployeeQueryVariables
>

export const EmployeeQueryContext = createContext<
  EmployeeQueryContextValue | undefined
>(undefined)

interface Props {
  children?: ReactNode
  type: UserType
}

const MemoizedEmployeeQueryProvider = ({
  children,
  type
}: PropsWithChildren<Props>) => {
  const hasSession = useHasSession()
  const session = useSession()
  const locale = useLocale()
  const brand = useGraphQLBrand()

  const options = useMemo(
    (): useQuery.Options<EmployeeQuery, EmployeeQueryVariables> => ({
      fetchPolicy: 'cache-and-network',
      skip: !hasSession || type !== UserType.Employee,
      variables: {
        locale: locale,
        brand: brand
      }
    }),
    [hasSession, type, locale, brand]
  )

  const result = useQuery(employeeQuery, options)
  const refetch = useRef(result.refetch)
  refetch.current = result.refetch

  const [isValid, setIsValid] = useState(hasSession)

  const hadSession = useRef(hasSession)
  useLayoutEffect(() => {
    if (
      hasSession &&
      !hadSession.current &&
      session.type === UserType.Employee
    ) {
      setIsValid(false)
      refetch.current()
    }
    hadSession.current = hasSession
  }, [hasSession, session.type])

  useLayoutEffect(() => {
    if (
      hasSession &&
      session.type === UserType.Employee &&
      result.networkStatus === NetworkStatus.ready
    ) {
      if (!isValid) {
        setIsValid(true)
      }
    } else if (!hasSession) {
      if (isValid) {
        setIsValid(false)
      }
    }
  }, [hasSession, result.networkStatus, session.type, isValid])

  return (
    <EmployeeQueryContext.Provider value={isValid ? result : undefined}>
      {children}
    </EmployeeQueryContext.Provider>
  )
}

export const EmployeeQueryProvider = memo(MemoizedEmployeeQueryProvider)
